import { useNode } from "@craftjs/core";
import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  ACCORDION_DROPDOWN_BUTTON_STYLE,
  BoxWidthTypes,
} from "../../../../constants/segmentTools";
import { updateAccordionStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { sliderToOpacity, opacityToSlider } from "../../../../utils/helpers";
import FroalaTextEditor from "../../../FroalaEditor/children/FroalaTextEditor";
import SegmentIcon from "../../../SegmentSettings/children/SegmentIcon";
import ColorPopup from "../../Shared/ColorPopup";
import MarginBox from "../../Shared/MarginBox";
import SettingsSlider from "../../Shared/SettingsSlider";
import SizeBox from "../../Shared/SizeBoxes";
import AccordionRightDropDown from "./AccordionRightDropDown";
import AccordionTitleBackgroundControl from "./AccordionTitleBackgroundControl";

const AccordionSettings = () => {
  const {
    actions: { setProp },
    header,
    expanded,
    margin,
    prevMargin,
    isApplyMarginOnAllSides,
    allSideMargin,
    borderColor,
    borderWidth,
    borderRadius,
    headerBgColor,
    headerTextColor,
    contentBgColor,
    contentTextColor,
    selectedBoxSizeId,
    dropDownRightIcon,
    contentOpacity,
    dropShadowColor,
    selectedIcon,
    headerFill,
  } = useNode((node) => ({
    header: node.data.props.header,
    expanded: node.data.props.expanded,
    margin: node.data.props.margin,
    prevMargin: node.data.props.prevMargin,
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
    allSideMargin: node.data.props.allSideMargin,
    borderColor: node.data.props.borderColor,
    borderWidth: node.data.props.borderWidth,
    borderRadius: node.data.props.borderRadius,
    headerBgColor: node.data.props.headerBgColor,
    headerTextColor: node.data.props.headerTextColor,
    contentBgColor: node.data.props.contentBgColor,
    contentTextColor: node.data.props.contentTextColor,
    selectedBoxSizeId: node.data.props.selectedBoxSizeId,
    dropDownRightIcon: node.data.props.dropDownRightIcon,
    contentOpacity: node.data.props.contentOpacity,
    dropShadowColor: node.data.props.dropShadowColor,
    selectedIcon: node.data.props.selectedIcon,
    headerFill: node.data.props.headerFill,
  }));

  const dispatch = useDispatch();
  const { accordion: accordionProps } = useSelector(
    (state) => state.codeblockStyles
  );

  // useEffect(() => {
  //   // When the component mounts, update the accordionProps in Redux
  //   const updatedAccordionProps = {
  //     ...accordionProps,
  //     header: header || accordionProps.header,
  //     expanded: expanded,
  //     margin: margin || accordionProps.margin,
  //     isApplyMarginOnAllSides: isApplyMarginOnAllSides,
  //     allSideMargin: allSideMargin || accordionProps.allSideMargin,
  //     borderRadius: borderRadius || accordionProps.borderRadius,
  //     borderWidth: borderWidth || accordionProps.borderWidth,
  //     borderColor: borderColor || accordionProps.borderColor,
  //     dropShadowColor: dropShadowColor || accordionProps.dropShadowColor,
  //     headerBgColor: headerBgColor || accordionProps.headerBgColor,
  //     headerTextColor: headerTextColor || accordionProps.headerTextColor,
  //     contentBgColor: contentBgColor || accordionProps.contentBgColor,
  //     contentTextColor: contentTextColor || accordionProps.contentTextColor,
  //     selectedBoxSizeId: selectedBoxSizeId || accordionProps.selectedBoxSizeId,
  //     dropDownRightIcon: dropDownRightIcon !== undefined ? dropDownRightIcon : accordionProps.dropDownRightIcon,
  //     headerOpacity: headerOpacity !== undefined ? headerOpacity : accordionProps.headerOpacity,
  //     contentOpacity: contentOpacity !== undefined ? contentOpacity : accordionProps.contentOpacity,
  //     selectedIcon: selectedIcon || accordionProps.selectedIcon
  //   };

  //   // Dispatch to update the global accordion props
  //   dispatch(updateAccordionStyles(updatedAccordionProps));
  // }, []);

  const handleAdjustment = (side, operation) => {
    const newValue =
      operation === "increase" ? margin[side] + 1 : margin[side] - 1;
    if (newValue >= 0) handleChange("margin", newValue, side);
  };

  const editorRef = useRef(null);

  const handleChange = (type, value, side, throttleRate) => {
    setProp((props) => {
      switch (type) {
        case "margin":
          if (value >= 0 && value <= 100) props.margin[side] = value;
          break;
        case "allSideMargin":
          props.allSideMargin = value;
          break;
        case "marginCheckbox":
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          break;
        case "headerBgColor":
          props.headerBgColor = value;
          break;
        case "contentBgColor":
          props.contentBgColor = value;
          break;
        case "resetContentBgColor":
          props.contentBgColor = "#F7F2FA";
          break;
        case "borderRadius":
          props.borderRadius = value;
          break;
        case "contentOpacity":
          props.contentOpacity = sliderToOpacity(value);
          break;
        case "borderColor":
          props.borderColor = value;
          break;
        case "borderWidth":
          props.borderWidth = value;
          break;
        case "dropShadowColor":
          props.dropShadowColor = value;
          break;
        case "resetColor":
          props.borderColor = null;
          break;
        case "resetShadow":
          props.dropShadowColor = null;
          break;
        case "selectedIcon":
          props.selectedIcon = value;
          break;
        case "headerFill":
          if (value) props.headerFill = value;
          else delete props.headerFill;
          break;
        default:
          break;
      }
      // SettingsSlider forwards a per-gesture throttleRate hint (see
      // GORDON LAW 2026-09-15 03:25 PT) so every setProp call from ONE drag
      // gesture merges into a single undo/history entry regardless of how
      // long the drag lasts; non-slider callers (e.g. typing) omit it and
      // keep the existing 500ms default.
    }, throttleRate ?? 500);

    // Update Redux state
    const updatedAccordionProps = { ...accordionProps };

    switch (type) {
      case "margin":
        if (side && value >= 0 && value <= 100) {
          updatedAccordionProps.margin = {
            ...updatedAccordionProps.margin,
            [side]: value,
          };
        }
        break;
      case "allSideMargin":
        updatedAccordionProps.allSideMargin = value;
        break;
      case "marginCheckbox":
        updatedAccordionProps.isApplyMarginOnAllSides =
          !isApplyMarginOnAllSides;
        break;
      case "headerBgColor":
        updatedAccordionProps.headerBgColor = value;
        break;
      case "contentBgColor":
        updatedAccordionProps.contentBgColor = value;
        break;
      case "resetContentBgColor":
        updatedAccordionProps.contentBgColor = "#F7F2FA";
        break;
      case "borderRadius":
        updatedAccordionProps.borderRadius = value;
        break;
      case "contentOpacity":
        updatedAccordionProps.contentOpacity = sliderToOpacity(value);
        break;
      case "borderColor":
        updatedAccordionProps.borderColor = value;
        break;
      case "borderWidth":
        updatedAccordionProps.borderWidth = value;
        break;
      case "dropShadowColor":
        updatedAccordionProps.dropShadowColor = value;
        break;
      case "resetColor":
        updatedAccordionProps.borderColor = null;
        break;
      case "resetShadow":
        updatedAccordionProps.dropShadowColor = null;
        break;
      case "selectedIcon":
        updatedAccordionProps.selectedIcon = value;
        break;
    }

    dispatch(updateAccordionStyles(updatedAccordionProps));
  };

  // The single patch entry point for AccordionTitleBackgroundControl (the
  // standard SegmentFillPanel/SegmentColor composition — see that file). A
  // "color" pick clears any structured fill and writes the legacy
  // headerBgColor field (with its Redux mirror) via the existing handleChange
  // path; a structured fill (gradient/pattern/image/gif) writes only
  // `headerFill`. No opacity, no auto-contrast toggle, no apply-to-all: the
  // title + dropdown icon always auto-contrast (Accordian/index.jsx), and
  // each header is styled individually.
  const handleTitleBackgroundPatch = (patch) => {
    if ("headerBgColor" in patch) handleChange("headerBgColor", patch.headerBgColor);
    if ("headerFill" in patch) handleChange("headerFill", patch.headerFill);
  };

  // GORDON LAW 2026-09-15 (gordon-0915-law-full-width-locks-edges-hides-margin-
  // rounding): Full Width locks the block flush to both edges (margin.left/right
  // = 0) and turning it back off restores whatever left/right values were set
  // before — not a hardcoded default. The pre-Full-Width values are stashed on
  // the node itself (props.prevMargin) so the restore survives a re-select/
  // reload, not just a component remount.
  const handleBoxSelect = (selectedBoxId) => {
    const goingFullWidth = selectedBoxId === BoxWidthTypes.FullWidth.id;
    setProp((props) => {
      if (goingFullWidth) {
        props.prevMargin = { left: props.margin.left, right: props.margin.right };
        props.margin = { ...props.margin, left: 0, right: 0 };
      } else {
        const restore = props.prevMargin || { left: 10, right: 10 };
        props.margin = { ...props.margin, left: restore.left, right: restore.right };
      }
      props.selectedBoxSizeId = selectedBoxId;
    });

    // Update Redux state
    const updatedAccordionProps = { ...accordionProps };
    if (goingFullWidth) {
      updatedAccordionProps.prevMargin = {
        left: margin.left,
        right: margin.right,
      };
      updatedAccordionProps.margin = {
        ...updatedAccordionProps.margin,
        left: 0,
        right: 0,
      };
    } else {
      const restore = prevMargin || { left: 10, right: 10 };
      updatedAccordionProps.margin = {
        ...updatedAccordionProps.margin,
        left: restore.left,
        right: restore.right,
      };
    }
    updatedAccordionProps.selectedBoxSizeId = selectedBoxId;
    dispatch(updateAccordionStyles(updatedAccordionProps));
  };

  const handleDropDownitemSelect = (id) => {
    setProp((props) => (props.dropDownRightIcon = id));

    // Update Redux state
    const updatedAccordionProps = { ...accordionProps };
    updatedAccordionProps.dropDownRightIcon = id;
    dispatch(updateAccordionStyles(updatedAccordionProps));
  };

  const isFullWidth = selectedBoxSizeId === BoxWidthTypes.FullWidth.id;

  return (
    <div>
      {/* <SegmentHeader title="Accordion" handleDeleteSegment={() => {}} /> */}

      <SizeBox
        data={[
          {
            id: BoxWidthTypes.ContentWidth.id,
            title: "Box Size",
            image: BoxWidthTypes.ContentWidth.image,
          },
          {
            id: BoxWidthTypes.FullWidth.id,
            title: "Full Width",
            image: BoxWidthTypes.FullWidth.image,
          },
        ]}
        selectedItem={selectedBoxSizeId}
        onBoxSelect={handleBoxSelect}
        title="Box Size"
      />

      <FroalaTextEditor
        editorHeight={"80px"}
        name={"text_body_1"}
        id={1}
        value={header}
        onChange={(model) => {
          setProp((props) => {
            if (model) {
              return (props.header = model);
            }
          });

          // Update Redux state
          // if (model) {
          //   const updatedAccordionProps = { ...accordionProps };
          //   updatedAccordionProps.header = model;
          //   dispatch(updateAccordionStyles(updatedAccordionProps));
          // }
        }}
        editorRef={editorRef}
        label={"Title Text"}
        placeholder="Accordion Header"
        toolbarButtons={[
          "fontFamily",
          "fontSize",
          "textColorCustom",
          "bold",
          "italic",
          "underline",
          "emoticons",
          "specialCharacters",
        ]}
        type="title"
        characters="40"
      />

      <AccordionRightDropDown
        title="Dropdown Button Style"
        items={ACCORDION_DROPDOWN_BUTTON_STYLE}
        selectedItemId={dropDownRightIcon}
        handleItemSelect={handleDropDownitemSelect}
      />

      {/* <SegmentIcon
        selectedIcon={selectedIcon}
        onIconSelect={(icon) => {
          handleChange('selectedIcon', icon);
        }}
        title="Accordion Icon"
      /> */}

      <MarginBox
        margin={margin}
        increaseMargin={(side) => handleAdjustment(side, "increase")}
        decreaseMargin={(side) => handleAdjustment(side, "decrease")}
        isApplyMarginOnAllSides={isApplyMarginOnAllSides}
        handleMarginCheckBox={() => handleChange("marginCheckbox")}
        allSideMargin={allSideMargin}
        fullWidth={isFullWidth}
        handleSliderMarginChange={(value) =>
          handleChange("allSideMargin", value)
        }
        handleMarginChange={(side, newValue) =>
          handleChange("margin", newValue, side)
        }
      />

      {!isFullWidth && (
        <SettingsSlider
          title="Corner Rounding"
          sliderValue={borderRadius}
          onValueChange={(val, throttleRate) =>
            handleChange("borderRadius", val, undefined, throttleRate)
          }
          min={1}
        />
      )}

      <AccordionTitleBackgroundControl
        headerBgColor={headerBgColor}
        headerFill={headerFill}
        onPatch={handleTitleBackgroundPatch}
      />

      <ColorPopup
        title="Content Background"
        background={contentBgColor}
        handleChangeBackgroundColor={(val) =>
          handleChange("contentBgColor", val)
        }
        resetBackgroundColor={() => handleChange("resetContentBgColor")}
        sliderValue={opacityToSlider(contentOpacity)}
        sliderChange={(val) => handleChange("contentOpacity", val)}
        sliderBoxTitle={"Opacity"}
        showSuggestedColorPalette={true}
      />

      <ColorPopup
        title="Border"
        background={borderColor}
        handleChangeBackgroundColor={(val) => handleChange("borderColor", val)}
        resetBackgroundColor={() => handleChange("resetColor", null, null)}
        sliderBoxTitle={"Border Width"}
        sliderValue={borderWidth}
        sliderChange={(val) => handleChange("borderWidth", val)}
        sliderMinValue={0}
        sliderMaxValue={8}
        sliderSymbol={"px"}
        isHideGradient={true}
        showSuggestedColorPalette={true}
        isBorderSlider
      />

      <ColorPopup
        title="Drop Shadow"
        background={dropShadowColor}
        handleChangeBackgroundColor={(val) =>
          handleChange("dropShadowColor", val)
        }
        resetBackgroundColor={() => handleChange("resetShadow", null, null)}
        showSlider={false}
        isHideGradient={true}
        showSuggestedColorPalette={true}
      />
    </div>
  );
};

export default AccordionSettings;
